/**
 * Canonical community GHG sector snapshots from fetchGHGInventoryData (cp-ghg-contract / live place GHG page).
 * Does not read ghg_inventory_precalculated.
 */
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';
import {
  LIVE_CHART_SECTOR_LABELS,
  extractSectorValue,
  extractYearTotal,
  fetchGHGInventoryData,
} from './ghgInventoryCalculationService';

export interface LivePlaceGhgSnapshot {
  geofips: number;
  location_name: string;
  year: number;
  total_mt: number | null;
  ldv_mt: number | null;
  hdv_mt: number | null;
  elec_res_mt: number | null;
  elec_com_mt: number | null;
  elec_mt: number | null;
  ng_res_mt: number | null;
  ng_com_mt: number | null;
  ng_mt: number | null;
  ag_mt: number | null;
  offroad_mt: number | null;
  waste_mt: number | null;
}

function nullablePairSum(a: number | null, b: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

export function snapshotFromInventoryResult(
  geofips: number,
  location_name: string,
  result: GHGInventoryResult,
  year: number
): LivePlaceGhgSnapshot {
  const ldv_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.LDV, year);
  const hdv_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.HDV, year);
  const elec_res_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.ELEC_RES, year);
  const elec_com_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.ELEC_COM, year);
  const ng_res_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.NG_RES, year);
  const ng_com_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.NG_COM, year);
  const ag_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.AGRICULTURE, year);
  const offroad_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.OFFROAD, year);
  const waste_mt = extractSectorValue(result, LIVE_CHART_SECTOR_LABELS.WASTE, year);
  const total_mt = extractYearTotal(result, year);

  return {
    geofips,
    location_name,
    year,
    total_mt,
    ldv_mt,
    hdv_mt,
    elec_res_mt,
    elec_com_mt,
    elec_mt: nullablePairSum(elec_res_mt, elec_com_mt),
    ng_res_mt,
    ng_com_mt,
    ng_mt: nullablePairSum(ng_res_mt, ng_com_mt),
    ag_mt,
    offroad_mt,
    waste_mt,
  };
}

const inventoryByLocation = new Map<string, Promise<GHGInventoryResult>>();

/** In-flight dedupe per location for bulk views (maps, rankings). */
export function fetchLiveInventoryCached(locationName: string): Promise<GHGInventoryResult> {
  const key = locationName.trim();
  const existing = inventoryByLocation.get(key);
  if (existing) return existing;
  const p = fetchGHGInventoryData(key);
  inventoryByLocation.set(key, p);
  return p;
}

export function clearLiveInventorySessionCache(): void {
  inventoryByLocation.clear();
}

export async function fetchLivePlaceSnapshot(
  geofips: number,
  location_name: string,
  year: number
): Promise<LivePlaceGhgSnapshot> {
  const result = await fetchLiveInventoryCached(location_name);
  return snapshotFromInventoryResult(geofips, location_name, result, year);
}

export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const out: R[] = [];
  let i = 0;
  async function worker(): Promise<void> {
    while (i < items.length) {
      const idx = i++;
      out[idx] = await fn(items[idx]);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return out;
}
